import { useState, useEffect, useRef } from "react";
import { X, CheckCircle, Zap, Star, Upload, MessageCircle, Loader, AlertCircle } from "lucide-react";
import API, { formatError } from "../api";

const ADMIN_WA = "33777369462";
const PAYMENT_NUMBER = "77 338 90 95";

const PLANS = [
  {
    key: "FREE",
    label: "FREE",
    price: "Gratuit",
    priceNum: 0,
    features: ["5 leads/mois", "Tableau de bord vendeur", "1 boutique"],
    color: "border-gray-200 bg-gray-50",
    badge: null,
    cta: null,
  },
  {
    key: "PRO",
    label: "PRO",
    price: "10 000 FCFA/mois",
    priceNum: 10000,
    features: ["Leads illimités", "Contact client direct", "Priorité dans le catalogue", "Support WhatsApp dédié"],
    color: "border-[#16A34A] bg-green-50",
    badge: "Recommandé",
    cta: "Passer PRO",
  },
  {
    key: "PREMIUM",
    label: "PREMIUM",
    price: "25 000 FCFA/mois",
    priceNum: 25000,
    features: ["Tout ce qui est PRO", "Mise en avant homepage", "Badge Vendeur Premium", "Statistiques avancées"],
    color: "border-[#F59E0B] bg-amber-50",
    badge: "Elite",
    cta: "Passer PREMIUM",
  },
];

export default function SubscriptionModal({ currentPlan = "FREE", pendingRequest, onClose, onSuccess }) {
  const [step, setStep] = useState(pendingRequest ? "pending" : "plans");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("wave");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  // Handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Image trop grande (max 5 MB)"); return; }
    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
    // Upload immediately
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.url || data.filename || "");
    } catch (e) {
      setError("Erreur upload: " + formatError(e));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!whatsapp.trim()) { setError("Entrez votre numéro WhatsApp"); return; }
    setSubmitting(true);
    setError("");
    try {
      await API.post("/api/subscriptions/request", {
        plan: selectedPlan.key,
        vendor_name: "",
        whatsapp: whatsapp.trim(),
        payment_method: paymentMethod,
        proof_image_url: proofUrl,
      });
      setStep("pending");
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-3xl">
          <h2 className="font-black text-gray-900 text-base">
            {step === "plans" && "Choisissez votre plan"}
            {step === "checkout" && `Passer ${selectedPlan?.label}`}
            {step === "pending" && "Paiement en vérification"}
          </h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5">
          {/* ===== STEP: PLANS ===== */}
          {step === "plans" && (
            <div className="space-y-3">
              {PLANS.map((plan) => (
                <div
                  key={plan.key}
                  className={`border-2 rounded-2xl p-4 relative transition-all ${plan.color} ${currentPlan === plan.key ? "opacity-60" : ""}`}
                >
                  {plan.badge && (
                    <span className={`absolute top-3 right-3 text-[10px] font-black px-2 py-0.5 rounded-full ${plan.key === "PRO" ? "bg-[#16A34A] text-white" : "bg-[#F59E0B] text-white"}`}>
                      {plan.badge}
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${plan.key === "PRO" ? "bg-[#16A34A]" : plan.key === "PREMIUM" ? "bg-[#F59E0B]" : "bg-gray-300"}`}>
                      {plan.key === "PREMIUM" ? <Star size={14} className="text-white" /> : <Zap size={14} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-black text-gray-900">{plan.label}</span>
                        <span className="text-xs font-bold text-gray-500">{plan.price}</span>
                      </div>
                      <ul className="space-y-0.5">
                        {plan.features.map((f) => (
                          <li key={f} className="text-xs text-gray-600 flex items-center gap-1.5">
                            <CheckCircle size={10} className="text-green-500 shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {plan.cta && currentPlan !== plan.key && (
                    <button
                      onClick={() => { setSelectedPlan(plan); setStep("checkout"); }}
                      className={`mt-3 w-full font-bold py-2.5 rounded-2xl text-sm transition-all hover:-translate-y-0.5 ${plan.key === "PRO" ? "bg-[#16A34A] text-white hover:bg-[#166534]" : "bg-[#F59E0B] text-white hover:bg-[#D97706]"}`}
                      data-testid={`select-plan-${plan.key.toLowerCase()}`}
                    >
                      {plan.cta}
                    </button>
                  )}
                  {currentPlan === plan.key && (
                    <div className="mt-2 text-xs text-center text-gray-500 font-semibold">Plan actuel</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ===== STEP: CHECKOUT ===== */}
          {step === "checkout" && selectedPlan && (
            <div className="space-y-5">
              {/* Payment instructions */}
              <div className="bg-[#F0FDF4] border border-green-200 rounded-2xl p-4">
                <p className="font-bold text-sm text-gray-900 mb-3">Instructions de paiement</p>
                <div className="flex gap-3 mb-3">
                  {["wave", "orange"].map((m) => (
                    <button
                      key={m}
                      onClick={() => setPaymentMethod(m)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${paymentMethod === m ? (m === "wave" ? "bg-[#1CBBEE] text-white border-[#1CBBEE]" : "bg-[#FF6600] text-white border-[#FF6600]") : "border-gray-200 text-gray-600"}`}
                    >
                      {m === "wave" ? "Wave" : "Orange Money"}
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Numéro :</span>
                    <span className="font-black text-gray-900">{PAYMENT_NUMBER}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nom :</span>
                    <span className="font-bold text-gray-900">Jokkoleen</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Montant :</span>
                    <span className="font-black text-[#16A34A] text-base">{selectedPlan.priceNum.toLocaleString("fr-FR")} FCFA</span>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, j'ai effectué le paiement pour le plan ${selectedPlan.label} (${selectedPlan.priceNum.toLocaleString("fr-FR")} FCFA via ${paymentMethod === "wave" ? "Wave" : "Orange Money"})`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-2.5 rounded-xl text-sm hover:bg-[#20BD5A] transition-colors"
                >
                  <MessageCircle size={14} /> Envoyer preuve via WhatsApp
                </a>
              </div>

              {/* Proof upload form */}
              <div>
                <p className="font-bold text-sm text-gray-900 mb-3">Formulaire de confirmation</p>

                <div className="mb-3">
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Votre numéro WhatsApp</label>
                  <input
                    type="tel"
                    placeholder="Ex: 77 338 90 95"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
                    data-testid="sub-whatsapp-input"
                  />
                </div>

                {/* Upload proof */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Capture d'écran du paiement</label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#16A34A] hover:bg-green-50 transition-all"
                    data-testid="proof-upload-zone"
                  >
                    {proofPreview ? (
                      <img src={proofPreview} alt="Preuve" className="max-h-40 mx-auto rounded-xl object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        {uploading ? <Loader size={22} className="animate-spin text-[#16A34A]" /> : <Upload size={22} />}
                        <p className="text-xs">{uploading ? "Upload en cours..." : "Cliquez pour ajouter la capture"}</p>
                      </div>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 rounded-xl px-3 py-2 mb-3">
                    <AlertCircle size={13} /> {error}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting || uploading}
                  className="w-full bg-[#16A34A] text-white font-black py-3.5 rounded-2xl text-sm hover:bg-[#166534] disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                  data-testid="submit-subscription-btn"
                >
                  {submitting ? "Envoi en cours..." : "J'ai effectué le paiement"}
                </button>

                <button onClick={() => setStep("plans")} className="w-full mt-2 text-gray-500 text-xs py-2 hover:text-gray-700">
                  Retour aux plans
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP: PENDING ===== */}
          {step === "pending" && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader size={28} className="text-amber-500 animate-spin" />
              </div>
              <h3 className="font-black text-gray-900 text-base mb-2">Paiement en cours de vérification</h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                Notre équipe vérifie votre paiement. Vous recevrez une confirmation sous 24h via WhatsApp.
              </p>
              <a
                href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent("Bonjour Jokkoleen, j'ai soumis une demande d'abonnement et j'aimerais vérifier son statut.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-5 py-2.5 rounded-2xl text-sm hover:bg-[#20BD5A] transition-colors"
              >
                <MessageCircle size={14} /> Contacter le support
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
